import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSettings, updateSettings, initializeSettings } from '../services/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Settings, Save, Loader2, Building2, FileText, Sun } from 'lucide-react';
import { toast } from 'sonner';

export const SettingsPage = () => {
  const { userData, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: 'SUNSTORE KOLHAPUR',
    logo_url: '',
    address: '',
    phone: '',
    email: '',
    gst_number: '',
    state_code: '27',
    pdf_footer: 'Thank You for Your Opportunity!\nWe look forward to continued support in our mutual interest.\nSUNSTORE KOLHAPUR\nComputer Generated Document – No Signature Required'
  });

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied. Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchSettings();
  }, [userData]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      await initializeSettings();
      const settings = await getSettings();
      setFormData({
        company_name: settings.company_name || 'SUNSTORE KOLHAPUR',
        logo_url: settings.logo_url || '',
        address: settings.address || '',
        phone: settings.phone || '',
        email: settings.email || '',
        gst_number: settings.gst_number || '',
        state_code: settings.state_code || '27',
        pdf_footer: settings.pdf_footer || 'Thank You for Your Opportunity!\nWe look forward to continued support in our mutual interest.\nSUNSTORE KOLHAPUR\nComputer Generated Document – No Signature Required'
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      await updateSettings(formData);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-4" data-testid="settings-loading">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-6 h-6 text-amber-500" />
          Settings
        </h1>
        <p className="text-slate-500 text-sm">Configure company branding and PDF settings</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-amber-500" />
              Company Information
            </CardTitle>
            <CardDescription>
              This information will appear on all generated PDFs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-sm font-medium">
                Company Name
              </Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Enter company name"
                className="h-12"
                data-testid="company-name-input"
              />
            </div>

            {/* Logo URL */}
            <div className="space-y-2">
              <Label htmlFor="logo_url" className="text-sm font-medium">
                Logo URL (Optional)
              </Label>
              <Input
                id="logo_url"
                name="logo_url"
                value={formData.logo_url}
                onChange={handleChange}
                placeholder="https://example.com/logo.png"
                className="h-12"
                data-testid="logo-url-input"
              />
              <p className="text-xs text-slate-500">
                Leave empty to display company name in text only
              </p>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address" className="text-sm font-medium">
                Company Address
              </Label>
              <Textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter full company address"
                rows={3}
                data-testid="address-input"
              />
            </div>

            {/* Phone & Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  className="h-12"
                  data-testid="phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="h-12"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* GST & State Code Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gst_number" className="text-sm font-medium">
                  Company GST Number
                </Label>
                <Input
                  id="gst_number"
                  name="gst_number"
                  value={formData.gst_number}
                  onChange={handleChange}
                  placeholder="e.g., 27AABCU9603R1ZM"
                  className="h-12 uppercase"
                  data-testid="gst-number-input"
                  maxLength={15}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state_code" className="text-sm font-medium">
                  Default State Code
                </Label>
                <Input
                  id="state_code"
                  name="state_code"
                  value={formData.state_code}
                  onChange={handleChange}
                  placeholder="27"
                  className="h-12"
                  data-testid="state-code-input"
                  maxLength={2}
                />
                <p className="text-xs text-slate-500">
                  Maharashtra = 27
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PDF Settings */}
        <Card className="form-section">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-amber-500" />
              PDF Settings
            </CardTitle>
            <CardDescription>
              Customize the footer text for generated PDFs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* PDF Footer */}
            <div className="space-y-2">
              <Label htmlFor="pdf_footer" className="text-sm font-medium">
                PDF Footer Text
              </Label>
              <Textarea
                id="pdf_footer"
                name="pdf_footer"
                value={formData.pdf_footer}
                onChange={handleChange}
                placeholder="Enter footer text for PDFs"
                rows={5}
                data-testid="pdf-footer-input"
              />
              <p className="text-xs text-slate-500">
                This text will appear at the bottom of all generated PDF documents
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card className="form-section bg-amber-50 border-amber-200">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg text-amber-900">
              <Sun className="w-5 h-5" />
              PDF Header Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h2 className="text-xl font-bold text-slate-900">{formData.company_name || 'SUNSTORE KOLHAPUR'}</h2>
              {formData.address && (
                <p className="text-sm text-slate-600 mt-1">{formData.address}</p>
              )}
              {(formData.phone || formData.email) && (
                <p className="text-sm text-slate-600 mt-1">
                  {formData.phone} {formData.phone && formData.email && '|'} {formData.email}
                </p>
              )}
              {formData.gst_number && (
                <p className="text-sm text-slate-600 mt-1">GST: {formData.gst_number}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12"
            onClick={() => navigate('/dashboard')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 h-12 solar-gradient"
            disabled={saving}
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsPage;
